import { useRef, useState } from 'react';

import { IntersectionObserverContext } from './IntersectionObserverContext';

/** This helper function can be added to JSON.stringify to remove circular references
 * from an object that needs to be stringified. Circular refs throw an error in JSON.stringify
 */
function flatted() {
  const seen = new WeakSet();
  return (_: any, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return;
      seen.add(value);
    }
    return value;
  };
}

const defaultOptions: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px 0px 0px',
  threshold: 0,
};

type IntersectionObserverProps = {
  children?: React.ReactNode;
};

export const IntersectionObserverProvider: React.FC<IntersectionObserverProps> = ({ children }) => {
  const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());
  const entriesRef = useRef<Map<Element, IntersectionObserverEntry>>(new Map());
  const [entries, setEntries] = useState<Map<Element, IntersectionObserverEntry>>(new Map());

  const handler: IntersectionObserverCallback = (observedEntries) => {
    observedEntries.forEach((entry) => entriesRef.current.set(entry.target, entry));
    setEntries(new Map(entriesRef.current));
  };

  const observe = (element: HTMLElement, options: IntersectionObserverInit = {}) => {
    const stringifiedOptions = JSON.stringify({ ...defaultOptions, ...options }, flatted());
    let targetObserver: IntersectionObserver;

    if (observersRef.current.has(stringifiedOptions)) {
      targetObserver = observersRef.current.get(stringifiedOptions)!;
    } else {
      targetObserver = new IntersectionObserver(handler, options);
      observersRef.current.set(stringifiedOptions, targetObserver);
    }

    targetObserver.observe(element);

    // console.log(observersRef.current);
  };

  const unobserve = (element: HTMLElement, options: IntersectionObserverInit = {}) => {
    observersRef.current.get(JSON.stringify({ ...defaultOptions, ...options }, flatted()))?.unobserve(element);
  };

  return (
    <IntersectionObserverContext.Provider value={{ observe, unobserve, entries }}>
      {children}
    </IntersectionObserverContext.Provider>
  );
};
